'use client';

import React, { useState, useRef, useEffect } from 'react';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { Spinner } from '@/core/presentation/components/ui/Spinner';
import { APP_SECTIONS, APP_MENU_FOOTER_GROUP } from '../../constants/sections';
import type { AppSectionId } from '../../constants/sections';
import {
  APP_HEADER_LOGGING_OUT,
  APP_HEADER_LOGOUT,
} from '@/features/app/presentation/constants/app-shell-copy';

const sectionsById = Object.fromEntries(APP_SECTIONS.map((s) => [s.id, s]));

export interface AppHeaderAccountProps {
  currentSection: AppSectionId;
  onSectionChange: (section: AppSectionId) => void;
  companyName?: string;
  /** Chamado ao selecionar uma opção (ex.: fechar sidebar no mobile) */
  onSelectOption?: () => void;
  /** Sign out + redirecionar (definido na página). */
  onLogout?: () => void | Promise<void>;
  /** True enquanto o logout estiver em andamento. */
  isLoggingOut?: boolean;
}

export const AppHeaderAccount: React.FC<AppHeaderAccountProps> = ({
  currentSection,
  onSectionChange,
  companyName = 'Sua empresa',
  onSelectOption,
  onLogout,
  isLoggingOut = false,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isContaSection = APP_MENU_FOOTER_GROUP.sectionIds.includes(currentSection);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (sectionId: AppSectionId) => {
    onSectionChange(sectionId);
    onSelectOption?.();
    setDropdownOpen(false);
  };

  const handleLogoutClick = () => {
    onSelectOption?.();
    setDropdownOpen(false);
    void onLogout?.();
  };

  const hasFooterNav = APP_MENU_FOOTER_GROUP.sectionIds.length > 0;
  const hasMenuContent = hasFooterNav || onLogout != null;

  return (
    <div className="relative shrink-0" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => {
          if (!hasMenuContent) return;
          setDropdownOpen((o) => !o);
        }}
        disabled={!hasMenuContent}
        className={`flex items-center gap-2 px-2 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark ${
          isContaSection
            ? 'bg-cyan/20 text-cyan border border-cyan/30'
            : 'text-white/90 hover:bg-white/10 hover:text-white border border-transparent'
        } ${!hasMenuContent ? 'opacity-80 cursor-default' : ''}`}
        aria-expanded={dropdownOpen}
        aria-haspopup={hasMenuContent ? 'true' : undefined}
        aria-label={
          !hasMenuContent ? 'Conta'
          : dropdownOpen ?
            'Fechar opções da conta'
          : 'Abrir opções da conta'
        }
      >
        <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
          <AppIcon name="UserCircleIcon" size={22} className="text-white/90" />
        </div>
        <div className="min-w-0 max-w-[120px] hidden sm:block text-left">
          <p className="text-sm font-medium truncate leading-tight">Olá</p>
          <p className="text-xs text-white/70 truncate leading-tight">{companyName}</p>
        </div>
        {hasMenuContent ?
          <AppIcon
            name="ChevronDownIcon"
            size={18}
            className={`shrink-0 text-white/70 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
          />
        : null}
      </button>
      {hasMenuContent && dropdownOpen && (
        <div
          className="absolute right-0 top-full mt-1 py-1 min-w-[180px] rounded-lg static-gradient-bg border border-white/15 shadow-xl z-50"
          role="menu"
        >
          {hasFooterNav ?
            APP_MENU_FOOTER_GROUP.sectionIds.map((sectionId) => {
              const item = sectionsById[sectionId];
              if (!item) return null;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handleSelect(item.id)}
                  className={`flex items-center gap-2.5 w-full px-3 py-2 text-left text-sm font-medium transition-colors ${
                    currentSection === item.id
                      ? 'bg-cyan/20 text-cyan'
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <AppIcon name={item.iconName} size={18} className="shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })
          : null}
          {onLogout ?
            <>
              {hasFooterNav ?
                <div className="my-1 border-t border-white/10" role="separator" aria-hidden />
              : null}
              <button
                type="button"
                role="menuitem"
                onClick={handleLogoutClick}
                disabled={isLoggingOut}
                className="flex items-center gap-2.5 w-full px-3 py-2 text-left text-sm font-medium text-white/90 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoggingOut ?
                  <>
                    <Spinner size="sm" className="text-white/80 shrink-0" />
                    <span>{APP_HEADER_LOGGING_OUT}</span>
                  </>
                : <>
                    <AppIcon name="ArrowRightOnRectangleIcon" size={18} className="shrink-0" />
                    <span>{APP_HEADER_LOGOUT}</span>
                  </>
                }
              </button>
            </>
          : null}
        </div>
      )}
    </div>
  );
};
